import { Organization, BundleEntry, Practitioner, PractitionerRole } from "fhir/r4";
import { FunctionComponent, useEffect, useReducer, useRef, useState } from "react";
import { Button, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import styles from "./PractitionerForm.module.css";
import OrganizationForm from "../OrganizationForm/OrganizationForm";

const PractitionerForm: FunctionComponent<{
    submitTrigger?: boolean,
    resetTrigger?: boolean,
    displayButtons?: boolean,
    dataSubmit: (entries: BundleEntry[]) => void
}> = (props) => {
    const { t } = useTranslation();
    const submitPractitionerRef = useRef<() => void>(() => undefined);

    useEffect(() => {
        if (props.resetTrigger) {
            triggerReset();
            onReset();
        }
    }, [props.resetTrigger]);

    useEffect(() => {
        if (props.submitTrigger) {
            triggerSubmit();
        }
    }, [props.submitTrigger]);

    const [organization, setOrganization] = useState(undefined as Organization | undefined)
    const [createOrganization, setCreateOrganization] = useState(true);
    const [resetTrigger, triggerReset] = useReducer(x => x + 1, 0);
    const [submitTrigger, triggerSubmit] = useReducer(x => x + 1, 0);

    useEffect(() => {
        if (props.submitTrigger && createOrganization && organization !== undefined) {
            submitPractitionerRef.current();
        }
    }, [organization, props.submitTrigger, createOrganization])

    const [form, setForm] = useState({
        name: '',
        surname: '',
        birthdate: '',
        gender: 'unknown',
        rpps: '',
        profession: '',
        category: '',
        mode: '',
        activity_type: '',
        region: '',
    });

    const handleChange = (event: { target: { name: any; value: any; }; }) => {
        setForm({ ...form, [event.target.name]: event.target.value });
    }

    async function onSubmit(event: any) {
        const formtv = event.currentTarget;
        if (formtv.checkValidity() === true) {
            await triggerSubmit();
            generateAndSubmitPractitioner();
        }
    }

    function generateAndSubmitPractitioner() {
        const entries: BundleEntry[] = [];

        //Practitioner
        const practitionerUuid = "urn:uuid:0b8dc69d-2317-42f0-9b8d-1bc85b297210";
        const practitioner = {
            "resourceType": "Practitioner",
        } as Practitioner;

        practitioner.identifier = [
            {
                "use": "official",
                "type": {
                    "coding": [
                        {
                            "code": "RPPS",
                            "system": "http://interopsante.org/fhir/CodeSystem/fr-v2-0203"
                        }
                    ]
                },
                "system": "urn:oid:1.2.250.1.71.4.2.1",
                "value": form.rpps
            }
        ];

        practitioner.name = [
            {
                "family": form.name,
                "given": [
                    form.surname
                ]
            }
        ];

        practitioner.gender = form.gender as Practitioner["gender"];
        practitioner.birthDate = form.birthdate;

        entries.push({
            "fullUrl": practitionerUuid,
            "resource": practitioner,
            "request": {
                "method": "POST",
                "url": "/Practitioner"
            }
        } as BundleEntry)

        //Professional Role
        const professionalRoleUuid = "urn:uuid:605031c6-759e-423f-9f0f-b19005785e01"
        const professionalRole = {
            "resourceType": "PractitionerRole",
            "active": true,
            "practitioner": {
                "reference": practitionerUuid
            },
            "code": [
                {
                    "coding": [
                        {
                            "code": form.profession,
                            "system": "https://mos.esante.gouv.fr/NOS/TRE_G15-ProfessionSante/FHIR/TRE-G15-ProfessionSante"
                        }
                    ]
                },
                {
                    "coding": [
                        {
                            "code": form.category,
                            "system": "https://mos.esante.gouv.fr/NOS/TRE_R09-CategorieProfessionnelle/FHIR/TRE-R09-CategorieProfessionnelle"
                        }
                    ]
                }
            ]
        } as PractitionerRole;

        entries.push({
            "fullUrl": professionalRoleUuid,
            "resource": professionalRole,
            "request": {
                "method": "POST",
                "url": "/PractitionerRole"
            }
        } as BundleEntry)

        //Organization
        const organizationUuid = "urn:uuid:d08e45b4-2f67-4eb5-a09a-9515b0bd5791"
        entries.push({
            "fullUrl": organizationUuid,
            "resource": organization,
            "request": {
                "method": "POST",
                "url": "/Organization"
            }
        } as BundleEntry);

        //Organizational Role
        const organizationalRoleUuid = "urn:uuid:5f1ec2d7-ac6c-4fed-b513-3634824a59e4"
        const organizationalRole = {
            "resourceType": "PractitionerRole",
            "active": true,
            "code": [
                {
                    "coding": [
                        {
                            "system": "urn:oid:1.2.250.1.213.1.6.1.19",
                            "code": form.activity_type
                        }
                    ]
                },
                {
                    "coding": [
                        {
                            "system": "urn:oid:1.2.250.1.213.1.6.1.18",
                            "code": form.mode
                        }
                    ]
                }
            ],
            "extension": [
                {
                    "url": "https://apifhir.annuaire.sante.fr/ws-sync/exposed/structuredefinition/practitionerRole-partOf",
                    "valueReference": {
                        "reference": professionalRoleUuid
                    }
                },
                {
                    "url": "https://apifhir.annuaire.sante.fr/ws-sync/exposed/structuredefinition/practitionerRole-practiceRegion",
                    "valueCodeableConcept": {
                        "coding": [
                            {
                                "system": "https://mos.esante.gouv.fr/NOS/TRE_R30-RegionOM/FHIR/TRE-R30-RegionOM",
                                "code": form.region
                            }
                        ]
                    }
                }
            ],
            "organization": {
                "reference": organizationUuid,
            }
        } as PractitionerRole;

        entries.push({
            "fullUrl": organizationalRoleUuid,
            "resource": organizationalRole,
            "request": {
                "method": "POST",
                "url": "/PractitionerRole"
            }
        } as BundleEntry);

        props.dataSubmit(entries);
    }

    submitPractitionerRef.current = generateAndSubmitPractitioner;

    const onReset = () => {
        setForm({
            name: '',
            surname: '',
            birthdate: '',
            gender: 'unknown',
            rpps: '',
            profession: '',
            category: '',
            mode: '',
            activity_type: '',
            region: '',
        });
    }


    return <><Form.Group
        className="col"
    >
        <Form.Label>{t("forms.common.name")} :</Form.Label>
        <Form.Control
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            className="form-control"
            required />
        <Form.Control.Feedback type="invalid">
            {t("forms.practitioner.invalidName")}
        </Form.Control.Feedback>
    </Form.Group>
        <Form.Group className="col">
            <Form.Label>{t("forms.common.firstName")} :</Form.Label>
            <Form.Control
                type="text"
                name="surname"
                value={form.surname}
                onChange={handleChange}
                className="form-control"
                required />
            <Form.Control.Feedback type="invalid">
                {t("forms.practitioner.invalidFirstName")}
            </Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="col">
            <Form.Label>{t("forms.common.birthDate")} :</Form.Label>
            <Form.Control
                type="date"
                name="birthdate"
                value={form.birthdate}
                onChange={handleChange}
                className="form-control"
                required
            />
            <Form.Control.Feedback type="invalid">
                {t("forms.practitioner.invalidBirthDate")}
            </Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="col">
            <Form.Label>{t("forms.common.gender")} :</Form.Label>
            <Form.Select
                className="form-control"
                name="gender"
                value={form.gender}
                onChange={handleChange}
            >
                {(["unknown", "female", "male", "other"] as const).map((gender) => <option key={gender} value={gender}>{t(`forms.common.genderOptions.${gender}`)}</option>)}
            </Form.Select>
        </Form.Group>

        <Form.Group className="col">
            <Form.Label>{t("forms.practitioner.rpps")} :</Form.Label>
            <Form.Control
                type="text"
                name="rpps"
                value={form.rpps}
                onChange={handleChange}
                className="form-control"
                required />
            <Form.Control.Feedback type="invalid">
                {t("forms.practitioner.invalidRpps")}
            </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="col">
            <Form.Label>{t("forms.practitioner.profession")} :</Form.Label>
            <Form.Select
                className="form-control"
                name="profession"
                value={form.profession}
                onChange={handleChange}
            >
                <option value="">{t("forms.common.choose")}</option>
                {(["60", "10", "70", "69", "50", "40"] as const).map((profession) => <option key={profession} value={profession}>{t(`forms.practitioner.professionOptions.${profession}`)}</option>)}
            </Form.Select>
        </Form.Group>

        <Form.Group className="col">
            <Form.Label>{t("forms.practitioner.category")} :</Form.Label>
            <Form.Select
                className="form-control"
                name="category"
                value={form.category}
                onChange={handleChange}
            >
                <option value="">{t("forms.common.choose")}</option>
                {(["C", "E", "F", "M"] as const).map((category) => <option key={category} value={category}>{t(`forms.practitioner.categoryOptions.${category}`)}</option>)}
            </Form.Select>
        </Form.Group>

        <Form.Group className="col">
            <Form.Label>{t("forms.practitioner.mode")} :</Form.Label>
            <Form.Select
                className="form-control"
                name="mode"
                value={form.mode}
                onChange={handleChange}
            >
                <option value="">{t("forms.common.choose")}</option>
                {(["S", "L", "B"] as const).map((mode) => <option key={mode} value={mode}>{t(`forms.practitioner.modeOptions.${mode}`)}</option>)}
            </Form.Select>
        </Form.Group>

        <Form.Group className="col">
            <Form.Label>{t("forms.practitioner.activityType")} :</Form.Label>
            <Form.Select
                className="form-control"
                name="activity_type"
                value={form.activity_type}
                onChange={handleChange}
            >
                <option value="">{t("forms.common.choose")}</option>
                {(["GENR01", "GENR02", "GENR03", "GENR04", "GENR06", "GENR08", "GENR09", "GENR10", "GENR11", "GENR12"] as const).map((activity) => <option key={activity} value={activity}>{t(`forms.practitioner.activityOptions.${activity}`)}</option>)}
            </Form.Select>
        </Form.Group>

        <Form.Group className="col">
            <Form.Label>{t("forms.practitioner.region")} :</Form.Label>
            <Form.Select
                className="form-control"
                name="region"
                value={form.region}
                onChange={handleChange}
            >
                <option value="">{t("forms.common.choose")}</option>
                <option value="01">Guadeloupe</option>
                <option value="02">Martinique</option>
                <option value="03">Guyane</option>
                <option value="04">La Réunion</option>
                <option value="06">Mayotte</option>
                <option value="11">Ile-de-france</option>
                <option value="24">Centre-Val-de-Loire</option>
                <option value="27">Bourgogne-Franche-Comté</option>
                <option value="28">Normandie</option>
                <option value="32">Hauts-de-France</option>
                <option value="44">Grand-Est</option>
                <option value="52">Pays de la Loire</option>
                <option value="53">Bretagne</option>
                <option value="75">Nouvelle-Aquitaine</option>
                <option value="76">Occitanie</option>
                <option value="84">Auvergne-Rhône-Alpes</option>
                <option value="93">Provence-Alpes-Côte d'Azur</option>
                <option value="94">Corse</option>
                <option value="975">Saint-Pierre-et-Miquelon</option>
                <option value="977">Saint-Barthélemy</option>
                <option value="978">Saint-Martin</option>
                <option value="984">Terres australes françaises</option>
                <option value="986">Wallis-et-Futuna</option>
                <option value="987">Polynésie française</option>
                <option value="988">Nouvelle-Calédonie</option>
                <option value="989">Ile de Clipperton</option>
                <option value="99138">Monaco</option>
            </Form.Select>
        </Form.Group>

        <Form.Group className="col">
            <Form.Label>{t("forms.practitioner.organization")} :</Form.Label>
            <Form.Check
                type="switch"
                label={t("forms.practitioner.createOrganization")}
                checked={createOrganization}
                onChange={() => setCreateOrganization(!createOrganization)}
                isInvalid={!createOrganization}
            />

            {createOrganization ? (
                <Form.Group className={styles.subForm}>
                    <OrganizationForm submitTrigger={submitTrigger} resetTrigger={resetTrigger} organizationSubmit={setOrganization} />
                </Form.Group>
            ) : (
                <div className="invalid-feedback" style={{ display: "flex" }}>{t("forms.practitioner.existingOrganizationUnavailable")}</div>
            )}

        </Form.Group>


        {props.displayButtons &&
            <Row className="mb-3">
                <Form.Group className="col">

                    <Button
                        className="button"
                        variant="danger"
                        onClick={onSubmit}
                    >
                        {t("forms.common.submit")}
                    </Button>
                    <Button
                        className="button"
                        variant="secondary"
                        onClick={onReset}
                    >
                        {t("forms.common.reset")}
                    </Button>
                </Form.Group>
            </Row>
        }
    </>;
}

export default PractitionerForm;
